import { useCallback, useEffect, useState } from 'react';

import { clearListViewSnapshot, readListViewSnapshot, writeListViewSnapshot } from './listViewSessionPersistence';

/**
 * DataGrid 페이지네이션 + 화면별 필터 객체를 sessionStorage와 동기화한다.
 * 상세 화면을 다녀온 뒤 목록으로 돌아와도 이전 페이지·검색 조건을 유지한다.
 *
 * @param {string} storageKey - LIST_VIEW_SESSION_KEYS 항목
 * @param {{ page: number, pageSize: number }} defaultPagination - MUI DataGrid용 (page는 0부터)
 * @param {Record<string, unknown>} defaultFilters - 직렬화 가능한 필터(화면마다 구조 상이)
 * @returns {{
 *   paginationModel: { page: number, pageSize: number },
 *   setPaginationModel: import('react').Dispatch<import('react').SetStateAction<{ page: number, pageSize: number }>>,
 *   filters: Record<string, unknown>,
 *   setFilters: import('react').Dispatch<import('react').SetStateAction<Record<string, unknown>>>,
 *   resetListSession: () => void,
 * }}
 */
export const useListViewSessionPersistence = (storageKey, defaultPagination, defaultFilters) => {
  const [paginationModel, setPaginationModel] = useState(() => {
    const saved = readListViewSnapshot(storageKey);
    const p = saved?.pagination;
    if (
      p &&
      typeof p.page === 'number' &&
      p.page >= 0 &&
      typeof p.pageSize === 'number' &&
      p.pageSize > 0
    ) {
      return { page: p.page, pageSize: p.pageSize };
    }
    return { ...defaultPagination };
  });

  const [filters, setFilters] = useState(() => {
    const saved = readListViewSnapshot(storageKey);
    if (saved?.filters != null && typeof saved.filters === 'object' && !Array.isArray(saved.filters)) {
      return { ...defaultFilters, ...saved.filters };
    }
    return { ...defaultFilters };
  });

  useEffect(() => {
    writeListViewSnapshot(storageKey, { pagination: paginationModel, filters });
  }, [storageKey, paginationModel, filters]);

  const resetListSession = useCallback(() => {
    setPaginationModel({ ...defaultPagination });
    setFilters({ ...defaultFilters });
    clearListViewSnapshot(storageKey);
  }, [storageKey, defaultPagination, defaultFilters]);

  return {
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetListSession,
  };
};
